import { useDispatch, useSelector } from 'react-redux';
import { push } from 'connected-react-router';

import { Box, ButtonGroup, Heading, Modal, Paragraph, TextButton } from '@zillow/constellation-10';
import { closeNotification, showModalNotification, showPageNotification } from '@common/actions/NotificationActions';
import type { ErrorInterface } from '@common/types';
import { getRachelDashboard } from '@renter-hub/actions/requests';
import { checkRequestActionSuccess, getSignInPage } from '@common/paymentsUtils/api';
import HTTP_STATUS from '@common/constants/HttpConstants';
import paths from '@renter-hub/pathsManifest';
import { formatDate } from '@common/paymentsUtils/dateUtils';
import { DEFAULT_ERROR, PAYMENT_CANCEL_SUCCESS } from '@renter-hub/constants/notifications';
import DebouncedButton from '@common/components/ConstellationDebouncedButton';
import NotificationContainer from '@common/components/NotificationContainer';
import userPersonas from '@common/constants/userPersonas';
import { logError } from '@zg-rentals/log-error';
import { useCancelRenterPaymentSubscriptionWebApiHandler_cancelRenterPaymentSubscription } from '@zg-rentals/ts-rent-payment-client-react-query';
import { PaymentCategory } from '@zg-rentals/ts-rent-payment-client';

interface Props {
  isMoveInCosts?: boolean;
  onClickForStorybook?: () => void;
}

const CancelPaymentModal = ({ isMoveInCosts = false, onClickForStorybook }: Props) => {
  const dispatch = useDispatch();
  const { recurring, nextPayable, propertyId } = useSelector((state) => ({
    // @ts-expect-error
    recurring: state.payments.renter.dashboard?.paymentDetails?.paymentSubscriptionDetails?.recurring || false,
    // @ts-expect-error
    nextPayable: state.payments.renter.dashboard?.nextPayable,
    // @ts-expect-error
    propertyId: state.payments.renter.dashboard?.propertyId,
  }));

  const { mutateAsync: cancelPaymentMutation } =
    useCancelRenterPaymentSubscriptionWebApiHandler_cancelRenterPaymentSubscription({
      onError: (error) => {
        logError({ error });
        dispatch(showModalNotification(DEFAULT_ERROR));
      },
    });

  const handleBackClick = () => {
    dispatch(closeNotification());
    dispatch(push(paths.baseUrl));
  };

  const handleSubmit = async () => {
    if (onClickForStorybook) {
      onClickForStorybook();
    }
    const paymentCategory = isMoveInCosts ? PaymentCategory.MOVE_IN_COSTS : PaymentCategory.RENT;

    try {
      dispatch(closeNotification());

      // Request to cancel the currently scheduled payment
      await cancelPaymentMutation({
        postData: {
          paymentCategory,
        },
      });

      // Request the latest renter status
      const renterStatusResponse = await dispatch(getRachelDashboard({ propertyId }));
      await checkRequestActionSuccess(renterStatusResponse);

      // Navigate to the dashboard
      dispatch(push(paths.baseUrl));

      // Show a success banner
      dispatch(showPageNotification(PAYMENT_CANCEL_SUCCESS));
    } catch (err) {
      const error = err as ErrorInterface;
      if (error.action && error.action.payload) {
        const { status } = error.action.payload;

        // If 401 status, redirect to sign-in
        if (status === HTTP_STATUS.SC_UNAUTHORIZED) {
          window.location = getSignInPage();
          return null;
        }
      }

      logError({ error: err });
      dispatch(showModalNotification(DEFAULT_ERROR));
      return { error: true };
    }
  };

  const getDueDate = () => {
    const { dueDate, isScheduled } = nextPayable;
    if (!dueDate || !isScheduled) {
      logError({
        error: new Error('Cancel payment modal - invalid nextPayable value.'),
        context: { nextPayable },
      });
      return 'next';
    }
    return formatDate(dueDate, 'MMMM do');
  };

  const headerText = isMoveInCosts
    ? 'Are you sure you want to stop your move-in payment?'
    : recurring
      ? 'Are you sure you want to stop all rent payments?'
      : `Are you sure you want to stop your ${getDueDate()} rent payment?`;

  const bodyText = isMoveInCosts
    ? 'Your payment will be canceled right away. You will need to schedule a new payment to take care of your move-in costs.'
    : `Your ${recurring ? 'next ' : ''} payment will be canceled right away and none will be pending. ${
        recurring
          ? 'To pay your rent for this or future months, you will need to schedule payments.'
          : 'To pay your rent for this month, you will need to schedule a new payment.'
      }`;

  const submitButtonText = isMoveInCosts || !recurring ? 'Cancel payment' : 'Cancel recurring payments';

  return (
    <Modal
      trigger={null}
      open={true}
      size="sm"
      onOpenChange={(isOpen) => {
        if (!isOpen) {
          handleBackClick();
        }
      }}
      header={
        <Box css={{ padding: '16px 48px 16px 16px !important' }}>
          <Heading level={2} textStyle="heading-xs">
            {headerText}
          </Heading>
        </Box>
      }
      body={
        <Box css={{ padding: 'token(spacing.sm) !important' }}>
          <NotificationContainer modal persona={userPersonas.RENTER} />
          <Paragraph>{bodyText}</Paragraph>
        </Box>
      }
      footer={
        <Box css={{ padding: 'token(spacing.sm) !important' }}>
          <ButtonGroup aria-label="action-buttons" role="group">
            <TextButton onClick={handleBackClick}>Back</TextButton>
            <DebouncedButton emphasis="filled" onClick={handleSubmit}>
              {submitButtonText}
            </DebouncedButton>
          </ButtonGroup>
        </Box>
      }
    />
  );
};

export default CancelPaymentModal;
